import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Input, Table, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { d201Api, SCListItem } from '../../../services/d201Api';
import dayjs from 'dayjs';

interface SCSelectModalProps {
  open: boolean;
  onCancel: () => void;
  onSelect: (scNo: string) => void;
}

const columns: ColumnsType<SCListItem> = [
  { title: 'S/C No', dataIndex: 'sc_no', key: 'sc_no', width: 100 },
  { title: '客戶代號', dataIndex: 'customer_code', key: 'cust', width: 90 },
  { title: '客戶名稱', dataIndex: 'customer_name', key: 'custNm', ellipsis: true },
  {
    title: '日期', dataIndex: 'sc_date', key: 'date', width: 100,
    render: (v: string | null) => v ? dayjs(v).format('YYYY-MM-DD') : '-',
  },
  { title: '幣別', dataIndex: 'currency', key: 'cur', width: 60 },
  {
    title: '金額', dataIndex: 'total_amount', key: 'amt', width: 100, align: 'right',
    render: (v: number) => (v ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
  },
  { title: '品項數', dataIndex: 'item_count', key: 'cnt', width: 70, align: 'center' },
];

const SCSelectModal: React.FC<SCSelectModalProps> = ({ open, onCancel, onSelect }) => {
  const [keyword, setKeyword] = useState('');
  const [data, setData] = useState<SCListItem[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchList = useCallback(async (kw: string) => {
    setLoading(true);
    try {
      const items = await d201Api.listApprovedSCs(kw || undefined, 50);
      setData(items);
    } catch {
      message.error('載入 SC 清單失敗');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      setKeyword('');
      fetchList('');
    }
  }, [open, fetchList]);

  const handleSearch = () => {
    fetchList(keyword.trim());
  };

  return (
    <Modal
      title="選擇已核准 S/C 單"
      open={open}
      onCancel={onCancel}
      footer={null}
      width={800}
      destroyOnClose
    >
      <div className="flex gap-2 mb-3">
        <Input
          placeholder="輸入 S/C No 或客戶名稱搜尋"
          value={keyword}
          onChange={e => setKeyword(e.target.value)}
          onPressEnter={handleSearch}
          style={{ width: 300 }}
          allowClear
        />
      </div>
      <Table<SCListItem>
        dataSource={data}
        columns={columns}
        rowKey="sc_no"
        size="small"
        loading={loading}
        pagination={false}
        scroll={{ y: 400 }}
        onRow={record => ({
          onClick: () => onSelect(record.sc_no),
          style: { cursor: 'pointer' },
        })}
      />
    </Modal>
  );
};

export default SCSelectModal;
